/**
 * @file userway.js
 * @description This file is used to add the UserWay accessibility button to the website.
 * It creates a button that, when clicked, loads the UserWay scripts to enhance website accessibility.
 * The button can be positioned in various locations on the screen and is styled with a default color.
 * The default UserWay button is hidden, and the custom button is displayed instead. It handles the
 * UserWay widget's visibility and interaction based on user actions.
 * 
 * The UserWay scripts are only loaded if the user has previously interacted with the button,
 * which is determined by checking for the presence of a 'uw-uid' in localStorage
 *
 * The UserWay widget is opened automatically if the user clicks the button for the first time.
 * 
 * @requires window._userway_config - Configuration object for UserWay.
 * Add it in your HTML file like this:
 * <script>
 *  window._userway_config = {
 *    account: 'your-account-id', // Your UserWay account ID
 *    language: 'fr', // Language for the UserWay widget
 *    position: 5, // Position of the button (1-8)
 *    color: '#0000FF' // Color of the button
 *  };
 * </script>
 */

// SVG and CSS Configuration for the UserWay button. Edit as needed.
const svg = '<svg width="44" height="44" viewBox="0 0 60 60"><g fill="currentColor" fill-rule="evenodd"><circle cx="30" cy="30" r="30"/><path d="M30 4.429c14.123 0 25.571 11.448 25.571 25.571S44.123 55.571 30 55.571 4.429 44.123 4.429 30 15.877 4.429 30 4.429m0 2C16.982 6.429 6.429 16.982 6.429 30S16.982 53.571 30 53.571 53.571 43.018 53.571 30 43.018 6.429 30 6.429m10.594 18.207a1.57 1.57 0 0 1 1.042.87q.15.32.15.69a1.66 1.66 0 0 1-.392 1.04 1.62 1.62 0 0 1-.956.55 57 57 0 0 1-6.79.841 1.04 1.04 0 0 0-.747.388 1.1 1.1 0 0 0-.245.6l-.005.092.009.143.192 1.748a34.5 34.5 0 0 0 1.318 6.332l.247.769.268.768 1.242 3.38a1.61 1.61 0 0 1-.421 1.73 1.5 1.5 0 0 1-.516.314 1.6 1.6 0 0 1-.57.109 1.56 1.56 0 0 1-1.231-.642l-.106-.162-.076-.15-.317-.69a109 109 0 0 1-2.356-5.534l-.332-.86-.427 1.108q-.352.891-.721 1.776l-.374.885-.576 1.32-.593 1.31-.284.582a1.6 1.6 0 0 1-.824.894 1.56 1.56 0 0 1-1.214.041 1.59 1.59 0 0 1-1.006-1.701l.034-.186.05-.165 1.24-3.375a34 34 0 0 0 1.617-6.26l.117-.8.1-.814.2-1.739a1.13 1.13 0 0 0-.241-.837 1.08 1.08 0 0 0-.746-.393 57 57 0 0 1-6.847-.854 1.56 1.56 0 0 1-1.018-.667 1.6 1.6 0 0 1-.249-1.185 1.6 1.6 0 0 1 .653-1.033 1.56 1.56 0 0 1 1.19-.252 53.7 53.7 0 0 0 19.815.001c.23-.046.468-.04.696.018m-7.788-8.453a4.07 4.07 0 0 1 0 5.69 3.93 3.93 0 0 1-5.622 0 4.07 4.07 0 0 1 0-5.69 3.93 3.93 0 0 1 5.622 0" fill="#000" fill-rule="nonzero"/></g></svg>';
const css = `
    #userway-button {
        position: fixed;
        z-index: 9999999;
        cursor: pointer;
        width: 44px;
        height: 44px;
        margin: 13px;
    }
    #userway-button:hover{
        transform: scale(1.1);
    }
    @keyframes userway-scale {
        100% {
            transform: scale(1.1);
        }
    }
    @media (prefers-reduced-motion: no-preference) {
        #userway-button {
            transition: transform 0.1s ease-in-out;
        }
        #userway-button.loading{
            animation: userway-scale 0.4s alternate infinite ease-out;
        }
   }
`;

/**
 * Main entry point for the UserWay accessibility button.
 * This function is called when the DOM is fully loaded.
 * It checks for the UserWay configuration and initializes the button.
 * If the configuration is not found, it does nothing.
 */
document.addEventListener("DOMContentLoaded", function () {
    if (!window._userway_config) {
        // No configuration found
        return;
    }
    // Show the Userway button
    getUserWayButton(window._userway_config);
    // Load UserWay if needed
    loadUserWayScripts();
});

/**
 * Listen for clicks on the UserWay button.
 * When clicked, it loads the UserWay scripts if not already loaded,
 * or toggles the UserWay widget if it is already loaded.
 */
document.addEventListener("click", function (event) {
    const button = event.target.closest('#userway-button');
    if (!button) return;
    if (typeof UserWay !== 'undefined') {
        // If UserWay is already loaded, open the widget
        UserWay.widgetToggle();
    } else {
        // If UserWay is not loaded, load the scripts
        loadUserWayScripts(true);
    }
}, { passive: true });

/**
 * Function to create and display the UserWay accessibility button with a SVG.
 * @param Object configuration UserWay
 */
const getUserWayButton = function (config) {
    // Inject SVG and CSS into the document
    const userwayButton = document.createElement('div');
    userwayButton.id = 'userway-button';
    userwayButton.innerHTML = svg;
    // Add the CSS styles to the document head
    const style = document.createElement('style');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);

    const positions = [
        '',
        { top: '0', right: '0' }, // 1 - top right
        { top: '50%', right: '0' }, // 2 - center right
        { bottom: '0', right: '0' }, // 3 - bottom right
        { bottom: '0', left: '50%' }, // 4 -   bottom center
        { bottom: '0', left: '0' }, // 5 - bottom left
        { top: '50%', left: '0' }, // 6 - center left 
        { top: '0', left: '0' }, // 7 - top left
        { top: '0', left: '50%' }, // 8 - top center
    ];
    // Ajouter la classe de position
    const position = positions[config.position || 5];
    if (position) {
        Object.keys(position).forEach(key => {
            userwayButton.style[key] = position[key];
        });
    }
    userwayButton.style.color = config.color || '#0000FF';
    document.body.appendChild(userwayButton);
}

/**
 * Function to load the UserWay scripts asynchronously.
 * @param {boolean} force - If true, forces the loading of UserWay scripts even if 'uw-uid' is not set in localStorage.
 * This is useful for the first click to ensure the UserWay widget opens.
 * If false, it only loads the scripts if 'uw-uid' is present in localStorage.
 */
const loadUserWayScripts = function (force = false) {
    if (!force && !localStorage.getItem('uw-uid')) {
        return;
    }
    const s = document.createElement('script');
    s.src = 'https://cdn.userway.org/widget.js';
    s.async = true;
    s.setAttribute('data-account', window._userway_config.account || '');
    document.head.appendChild(s);
    document.getElementById('userway-button').classList.add('loading');
    // Set up an event listener to open the UserWay widget on first click
    document.addEventListener("userway:init_completed", function (e) {
        document.getElementById('userway-button').classList.remove('loading');
        const uw_instance = e.detail.userWayInstance;
        if (uw_instance) {
            if (force) uw_instance.widgetOpen();
            uw_instance.iconVisibilityOff();
        }

    });

}
